import { useMemo } from 'react';

import LoadingState from '@/components/LoadingState';
import { getDateandLabel, getTimeString } from '@/util/date';

import { useBroadcasts } from '../api/useBroadcasts';
import type { Broadcast } from '../types';

interface CommsBroadcastsListProps {
  selectedBroadcastId: string | null;
  onSelectBroadcast: (broadcastId: string) => void;
}

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  background: '#fff',
  overflowY: 'auto',
  width: '100%',
};

const rowBaseStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '12px 14px',
  cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6',
};

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: '#6b7280',
  padding: '10px 14px 4px',
};

const recalledTagStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 10,
  fontWeight: 600,
  color: '#b91c1c',
  background: '#fee2e2',
  padding: '1px 6px',
  borderRadius: 4,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

export function CommsBroadcastsList({
  selectedBroadcastId,
  onSelectBroadcast,
}: CommsBroadcastsListProps) {
  const { broadcasts, loading, error } = useBroadcasts({
    includeRecalled: true,
  });

  const sorted = useMemo(
    () =>
      [...broadcasts].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [broadcasts],
  );

  if (loading && broadcasts.length === 0) {
    return (
      <div style={containerStyle}>
        <LoadingState />
      </div>
    );
  }

  if (error && broadcasts.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: 16, color: '#b91c1c', fontSize: 13 }}>
          {error}
        </div>
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
          No broadcasts yet. Tap “New” to send one.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={sectionLabel}>Broadcasts</div>
      {sorted.map((broadcast) => (
        <BroadcastRow
          key={broadcast.id}
          broadcast={broadcast}
          isSelected={broadcast.id === selectedBroadcastId}
          onSelect={onSelectBroadcast}
        />
      ))}
    </div>
  );
}

interface BroadcastRowProps {
  broadcast: Broadcast;
  isSelected: boolean;
  onSelect: (broadcastId: string) => void;
}

function BroadcastRow({ broadcast, isSelected, onSelect }: BroadcastRowProps) {
  const dateLabel = labelFor(broadcast.created_at);
  const audienceSummary = summarizeAudience(broadcast);
  const recalled = !!broadcast.recalled_at;
  return (
    <div
      onClick={() => onSelect(broadcast.id)}
      style={{
        ...rowBaseStyle,
        background: isSelected ? '#eff6ff' : 'transparent',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: '#111827',
          }}
        >
          📣 {audienceSummary}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
          {dateLabel}
        </span>
      </div>
      <div
        style={{
          fontSize: 13,
          color: '#374151',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {broadcast.content}
      </div>
      {recalled ? <span style={recalledTagStyle}>Recalled</span> : null}
    </div>
  );
}

function summarizeAudience(broadcast: Broadcast): string {
  const f = broadcast.audience_filter;
  if (!f) return 'Workers';
  if (f.mode === 'direct') {
    const count = f.user_ids?.length ?? 0;
    return count === 1 ? '1 worker' : `${count} workers`;
  }
  const parts: string[] = [];
  const skillsCount = f.skills?.length ?? 0;
  if (skillsCount > 0)
    parts.push(`${skillsCount} skill${skillsCount === 1 ? '' : 's'}`);
  if (f.tax_types?.length) parts.push(f.tax_types.join('+').toUpperCase());
  if (f.location)
    parts.push(`${f.location.radius_miles}mi · ${f.location.zip_code}`);
  return parts.length > 0 ? `Workers · ${parts.join(' · ')}` : 'Workers';
}

function labelFor(iso: string): string {
  const d = new Date(iso);
  const today = getDateandLabel(new Date());
  const label = getDateandLabel(d);
  if (label === today) return getTimeString(d);
  return label;
}
